import { makeVar, useReactiveVar } from '@apollo/client';
import { uniqBy } from 'lodash';
import {
  SurfaceNodeType,
  SurfaceNode,
  SurfaceNodeUnion,
  SubSurfaceNode,
} from 'graphql/types';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useAuthorization from 'routes/surface/hooks/use-authorization';

export enum ClipboardAction {
  CUT,
  COPY,
  SYNC_COPY,
}

export interface ClipboardNode extends SurfaceNode {
  action: ClipboardAction;
}

interface ClipboardOperations {
  addNodeToClipboard: (node: SurfaceNodeUnion, action: ClipboardAction) => void;
  addNodesForCopyToClipboard: (nodes: SurfaceNodeUnion[]) => void;
  addNodesForCutToClipboard: (nodes: SurfaceNodeUnion[]) => void;
  removeNodeFromClipboard: (id: string) => void;
  addNodesToClipboard: (
    nodes: SurfaceNodeUnion[],
    action: ClipboardAction,
  ) => void;
  removeNodesFromClipboard: (nodes: SurfaceNodeUnion[]) => void;
  clearClipboard: () => void;
}

export const clipboardNodes = makeVar<ClipboardNode[]>([]);

export function useCanvasClipboard(): [ClipboardNode[], ClipboardOperations] {
  const canvasClipboard = useReactiveVar(clipboardNodes);

  const clipboardOperations = useCanvasClipboardOperations();

  return [canvasClipboard, clipboardOperations];
}

export function useCanvasClipboardOperations(): ClipboardOperations {
  const { canReadSurfaceNode } = useAuthorization();

  function addNodeToClipboard(
    node: SurfaceNodeUnion,
    action: ClipboardAction,
  ): void {
    if (node.type === SurfaceNodeType.SubSurface) {
      if (canReadSurfaceNode(node as SubSurfaceNode) === false) {
        return;
      }
    }

    const clipboardNode = { ...node, action };

    clipboardNodes([clipboardNode]);
  }

  function addNodesToClipboard(
    nodes: SurfaceNodeUnion[],
    action: ClipboardAction,
  ): void {
    const nodesForClipboard = nodes
      .filter((node) => {
        if (node.type === SurfaceNodeType.SubSurface) {
          return canReadSurfaceNode(node as SubSurfaceNode);
        }

        return true;
      })
      .map((nodes) => ({ ...nodes, action }));
    clipboardNodes(uniqBy(nodesForClipboard, 'id'));
  }

  function removeNodeFromClipboard(id: string): void {
    const isNodeInClipboard = clipboardNodes().some((node) => node.id === id);
    if (isNodeInClipboard) {
      clipboardNodes(clipboardNodes().filter((node) => node.id !== id));
    }
  }

  function removeNodesFromClipboard(nodes: SurfaceNodeUnion[]): void {
    const ids = nodes.map((node) => node.id);
    clipboardNodes(
      clipboardNodes().filter((node) => ids.includes(node.id) === false),
    );
  }

  function addNodesForCutToClipboard(nodes: SurfaceNodeUnion[]): void {
    addNodesToClipboard(nodes, ClipboardAction.CUT);
  }

  function addNodesForCopyToClipboard(nodes: SurfaceNodeUnion[]): void {
    addNodesToClipboard(nodes, ClipboardAction.COPY);
  }

  function clearClipboard() {
    clipboardNodes([]);
  }

  return {
    addNodeToClipboard,
    addNodesToClipboard,
    removeNodeFromClipboard,
    removeNodesFromClipboard,
    clearClipboard,
    addNodesForCutToClipboard,
    addNodesForCopyToClipboard,
  };
}
